/**
 * Timeline Band
 *
 * @module features/releases/components/TimelineBand
 * @description The pieces a release band (`TimelineRunEntry`) and a build row
 * (`TimelineBuildEntry`) share, so the two cannot drift apart: the rail
 * segment carried through the card, the card's selected-state classes, and the
 * single meta line.
 */

import React from 'react';
import { cn } from '@chatsdet/ui';

/**
 * Carries the rail through the card so the node reads as sitting on it. Same
 * `bg-border` token as the rail so it matches in both themes. Positioned
 * against the row's `relative` wrapper, not the card: the wrapper shares the
 * timeline container's left edge, so the rail's own `left-8` lands on the same
 * x, and `top-0 bottom-0` spans the `py-1` gap too. No z-index: a positioned
 * `z-index: auto` box paints over every non-positioned descendant of the card
 * (not just its background), which is fine because nothing else occupies that
 * column, and stays under the node's explicit `z-10`.
 */
export function BandRail() {
  return (
    <div
      aria-hidden="true"
      className="absolute left-8 top-0 bottom-0 w-[2px] rounded-full bg-border"
    />
  );
}

/**
 * The band card's class. A selected band bridges the gutter and runs flush
 * into the pane at `xl` (see `ReleasePropertyTimeline`) — the band's own
 * border is the visual connection, so its right edge must be square and
 * borderless, not closed off.
 */
export function bandCardClassName(isSelected: boolean): string {
  return cn(
    'gap-y-0',
    isSelected && 'border-primary ring-1 ring-primary/40 xl:-mr-6 xl:rounded-r-none xl:border-r-0'
  );
}

/** The band's single meta line: parts separated by a dot; clicking it selects the band. */
export function BandMetaLine({ parts, onClick }: { parts: React.ReactNode[]; onClick: () => void }) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1.5 cursor-pointer" onClick={onClick}>
      {parts.map((part, index) => (
        <React.Fragment key={index}>
          {index > 0 && <span className="text-muted-foreground/40">·</span>}
          {part}
        </React.Fragment>
      ))}
    </div>
  );
}
